import { OnaWordmark } from "@/assets/icons/OnaWordmark";
import { PREDEFINED_TASKS, type PredefinedTask } from "@/components/ai/ai-constants";
import { OnaVerificationDialog } from "@/components/ai/OnaVerificationDialog";
import { NewPromptInput, type PromptInputSubmission } from "@/components/ai/prompt-input/NewPromptInput";
import {
    isLoadingAtom,
    usePromptAtom,
    usePendingSubmission,
    selectedContextsAtom,
    selectedEnvironmentClassAtom,
    contextAssociatedProjectsAtom,
} from "@/components/ai/prompt-input/state";
import { useSubmitPrompts } from "@/components/ai/prompt-input/use-submit-prompt";
import { RecentAgentExecutions } from "@/components/ai/RecentAgentExecutions";
import { type TaskCard } from "@/components/ai/TaskCard";
import { PromptTemplate } from "@/components/flexkit/PromptTemplate";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { useDynamicTitle } from "@/hooks/use-dynamic-title";
import { useFlagOnaAIEnabled } from "@/hooks/use-flag";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useStripePaymentRedirectResult } from "@/hooks/use-stripe-payment-redirect-result";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { formatError } from "@/utils/errors";
import { Code, ConnectError } from "@connectrpc/connect";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { PaymentMethodStatus } from "gitpod-next-api/gitpod/v1/billing_pb";
import { atom, useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import { type FC, forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    clearOnboardingStateFromLocalStorage,
    clearPendingSubmissionFromLocalStorage,
    loadPendingSubmissionFromLocalStorage,
    savePendingSubmissionToLocalStorage,
} from "@/routes/ona-onboarding/local-storage";
import { useOrganization } from "@/queries/organization-queries";

const isVerificationDialogOpenAtom = atom(false);

export const OnaAIPage: FC = () => {
    useDynamicTitle("Ona");

    const location = useLocation();

    const contentRef = useRef<HTMLDivElement>(null);
    const { value: isOnaEnabled, loading: isLoadingOnaEnabled } = useFlagOnaAIEnabled();
    const setIsVerificationDialogOpen = useSetAtom(isVerificationDialogOpenAtom);
    const { pendingSubmissionAtom, clearPendingSubmission } = usePendingSubmission();
    const setPendingSubmission = useSetAtom(pendingSubmissionAtom);
    const setIsLoading = useSetAtom(isLoadingAtom);
    const setSelectedContexts = useSetAtom(selectedContextsAtom);
    const setSelectedEnvironmentClass = useSetAtom(selectedEnvironmentClassAtom);
    const setContextAssociatedProjects = useSetAtom(contextAssociatedProjectsAtom);
    const { data: organization } = useOrganization();
    const { data: user } = useAuthenticatedUser();
    const api = useGitpodAPI();
    const { submitPrompts } = useSubmitPrompts();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const segmentTrack = useSegmentTrack();
    const store = useStore();
    const navigate = useNavigate();
    const hasSubmittedFromLocation = useRef(false);
    const hasLoadedPendingSubmission = useRef(false);
    const previousOrgId = useRef<string | undefined>(undefined);

    const isPaymentMethodVerifiedQuery = queryOptions({
        queryKey: ["isPaymentMethodVerified", { organizationId: user?.organizationId }],
        queryFn: async () => {
            try {
                const response = await api.billingService.getBillingInfo({ organizationId: user?.organizationId });
                return response.paymentMethodStatus === PaymentMethodStatus.VERIFIED;
            } catch (error) {
                if (error instanceof ConnectError && error.code === Code.NotFound) {
                    return false;
                }
                throw error;
            }
        },
        enabled: !!user?.organizationId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });

    const handlePromptSubmission = useCallback(
        async (submission: PromptInputSubmission) => {
            setIsLoading(true);

            try {
                if (!organization) {
                    throw new Error("Organization not available");
                }

                // this will either return the cached billing info or wait for the query to finish fetching and return the result
                const isPaymentMethodVerified = await queryClient.fetchQuery(isPaymentMethodVerifiedQuery);

                if (isPaymentMethodVerified) {
                    await submitPrompts(submission);
                    clearPendingSubmission();
                } else {
                    // remember the submission in-memory for same-session verification flows
                    setPendingSubmission(submission);
                    // cache the submission in local storage to be submitted later in case the user is redirected away to complete verification
                    savePendingSubmissionToLocalStorage(submission, organization.id);
                    segmentTrack("onboarding_verification_required");
                    setIsVerificationDialogOpen(true);
                }
            } catch (error) {
                console.error("failed to submit tasks", error);
                toast({
                    title: "Failed to submit tasks",
                    description: formatError(error),
                });
            } finally {
                setIsLoading(false);
            }
        },
        [
            organization,
            queryClient,
            isPaymentMethodVerifiedQuery,
            submitPrompts,
            setPendingSubmission,
            segmentTrack,
            setIsVerificationDialogOpen,
            toast,
            setIsLoading,
            clearPendingSubmission,
        ],
    );

    useEffect(() => {
        if (organization?.id !== previousOrgId.current) {
            // Reset prompt input context when switching organizations
            if (previousOrgId.current !== undefined) {
                setSelectedContexts([]);
                setSelectedEnvironmentClass(null);
                setContextAssociatedProjects([]);
            }
            hasLoadedPendingSubmission.current = false;
            previousOrgId.current = organization?.id;
        }
    }, [organization?.id, setSelectedContexts, setSelectedEnvironmentClass, setContextAssociatedProjects]);

    useEffect(() => {
        if (!organization || hasLoadedPendingSubmission.current) {
            return;
        }

        const pendingSubmission = loadPendingSubmissionFromLocalStorage(organization.id);
        if (!pendingSubmission) {
            return;
        }

        hasLoadedPendingSubmission.current = true;
        setPendingSubmission(pendingSubmission);

        // the state is set when redirected from onboarding flow
        // before the onboarding flow ends, pending submission is stored in local storage
        if (location.state?.submitPendingPrompt && !hasSubmittedFromLocation.current) {
            hasSubmittedFromLocation.current = true;
            void handlePromptSubmission(pendingSubmission);
            // Clear the consumed submission from history state to avoid repeated submissions
            navigate(".", { replace: true, state: null });
        }
    }, [location.state?.submitPendingPrompt, handlePromptSubmission, navigate, setPendingSubmission, organization]);

    if (isLoadingOnaEnabled) {
        return;
    }

    async function handleVerificationSuccess() {
        if (!organization) {
            toast({
                title: "Unable to complete verification",
                description: "Organization not available. Please refresh the page and try again.",
            });
            return;
        }

        setIsVerificationDialogOpen(false);

        let _pendingSubmission: PromptInputSubmission | null = null;
        if (store.get(pendingSubmissionAtom)) {
            _pendingSubmission = store.get(pendingSubmissionAtom);
        } else {
            _pendingSubmission = loadPendingSubmissionFromLocalStorage(organization.id);
            setPendingSubmission(_pendingSubmission);
        }

        if (_pendingSubmission) {
            await submitPrompts(_pendingSubmission);
            clearPendingSubmissionFromLocalStorage(organization.id);
            setPendingSubmission(null);
            clearOnboardingStateFromLocalStorage();
        }

        toast({
            title: "Thank you!",
            description: "We've added $10 in OCUs to your account.",
        });

        setIsLoading(false);
    }

    return (
        <>
            <div data-testid="ona-ai-page" className="mx-auto flex max-w-[877px]">
                {isOnaEnabled ? (
                    <OnaAIContents ref={contentRef} onSubmitPrompt={handlePromptSubmission} />
                ) : (
                    <OnaAINotEnabled />
                )}
            </div>
            <_OnaVerificationDialog onVerificationSuccess={handleVerificationSuccess} />
        </>
    );
};

const _OnaVerificationDialog: FC<{
    onVerificationSuccess: () => void;
}> = ({ onVerificationSuccess }) => {
    const [isVerificationDialogOpen, setIsVerificationDialogOpen] = useAtom(isVerificationDialogOpenAtom);
    const { redirectResult, clearStripePaymentRedirectParams } = useStripePaymentRedirectResult();

    useEffect(() => {
        if (redirectResult?.redirectStatus === "succeeded") {
            setIsVerificationDialogOpen(true);
        }
    }, [redirectResult, setIsVerificationDialogOpen]);

    return (
        <OnaVerificationDialog
            open={isVerificationDialogOpen}
            onOpenChange={setIsVerificationDialogOpen}
            onVerificationSuccess={() => {
                onVerificationSuccess();
                void clearStripePaymentRedirectParams();
            }}
        />
    );
};

const OnaAINotEnabled: FC = () => {
    const { data: user } = useAuthenticatedUser();
    const [greeting, setGreeting] = useState("");

    useEffect(() => {
        const getTimeBasedGreeting = (): string => {
            const hour = new Date().getHours();
            let timeGreeting = "";

            if (hour >= 5 && hour < 12) {
                timeGreeting = "Good Morning";
            } else if (hour >= 12 && hour < 18) {
                timeGreeting = "Good Afternoon";
            } else if (hour >= 18 && hour < 22) {
                timeGreeting = "Good Evening";
            } else {
                timeGreeting = "Good Night";
            }

            const firstName = user?.name ? user.name.split(" ")[0] : undefined;

            return firstName ? `${timeGreeting}; ${firstName}` : timeGreeting;
        };

        setGreeting(getTimeBasedGreeting());
    }, [user]);

    return (
        <div data-testid="ona-ai-page-contents-disabled" className="flex flex-col items-center gap-6">
            <Heading1>{greeting}</Heading1>
            <Text className="text-center text-lg text-content-primary">Ona AI is not yet available to you.</Text>
        </div>
    );
};

const OnaAIContents = forwardRef<
    HTMLDivElement,
    {
        onSubmitPrompt: (submission: PromptInputSubmission) => void;
    }
>(({ onSubmitPrompt }, ref) => {
    const [taskCards, setTaskCards] = useState<TaskCard[]>([]);
    const promptAtom = usePromptAtom();
    const setPrompt = useSetAtom(promptAtom);
    const { pendingSubmissionAtom } = usePendingSubmission();
    const onboardingPendingSubmission = useAtomValue(pendingSubmissionAtom);

    const handlePromptClick = useCallback(
        (task: PredefinedTask) => {
            // Insert the prompt text into the prompt box instead of creating a card
            setPrompt(task.prompt);
        },
        [setPrompt],
    );

    const handleTaskCardDismiss = useCallback((taskName: PredefinedTask["taskName"]) => {
        setTaskCards((prev) => prev.filter((card) => card.prompt.taskName !== taskName));
    }, []);

    const handleTaskCardUse = useCallback(
        (prompt: string) => {
            setPrompt(prompt);
        },
        [setPrompt],
    );

    return (
        <div className="flex w-full flex-col items-center justify-center">
            <div
                ref={ref}
                data-testid="ona-ai-page-contents"
                className="flex w-full max-w-[46rem] flex-col gap-12 overflow-x-hidden px-1"
            >
                <div className="flex flex-col items-center gap-6 pt-2 md:gap-4 md:pt-20">
                    <Heading1>
                        <OnaWordmark className="h-8" />
                    </Heading1>

                    <Text className="text-center text-2xl text-content-primary">
                        What do you want to get done today?
                    </Text>
                    <div className="w-full min-w-full">
                        <NewPromptInput
                            selectedCards={taskCards.map((card) => card.prompt)}
                            onCardDismiss={handleTaskCardDismiss}
                            onCardUse={handleTaskCardUse}
                            onSubmit={onboardingPendingSubmission ? onSubmitPrompt : undefined}
                        />
                    </div>

                    <div
                        className="mt-2 flex w-full flex-row flex-wrap justify-center gap-4"
                        data-testid="example-prompts"
                    >
                        <div className="flex w-full flex-wrap justify-center gap-4 text-sm text-content-secondary">
                            {PREDEFINED_TASKS.map((prompt) => (
                                <PromptTemplate
                                    data-testid={`example-prompt-${prompt.taskName}`}
                                    data-tracking-id={`example-prompt-${prompt.taskName}-ona-ai-page`}
                                    Icon={prompt.Icon}
                                    key={prompt.taskName}
                                    iconColor={prompt.iconColor}
                                    iconBackgroundColor={prompt.iconBackgroundColor}
                                    title={prompt.title}
                                    description={prompt.description}
                                    selected={false}
                                    size="sm"
                                    onSelectChange={() => {
                                        handlePromptClick(prompt);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <RecentAgentExecutions />
            </div>
        </div>
    );
});
OnaAIContents.displayName = "OnaAIContents";
